import type { MosaicNode } from 'react-mosaic-component'
import { mosaicNodesEqual } from './layout-policy'

/**
 * Where a locked layout's panel settings are saved.
 *
 *  - `'browser'` (default): IndexedDB only, never the server. The Tags page,
 *    Dashboard Home and the Backtest report presets keep their view state in
 *    the browser on purpose.
 *  - `'server'`: saved to the user's profile like any layout, so settings
 *    follow them across devices. The History presets use this.
 *
 * Either way a locked layout's SHAPE comes from its preset in code.
 */
export type LockedPersistence = 'browser' | 'server'

/** Layout-store mode for a mosaic's `locked` / `lockedPersistence` props. */
export function lockedStoreMode(
  locked: boolean,
  persistence: LockedPersistence = 'browser',
): { localOnly: boolean; fixedShape: boolean } {
  if (!locked) return { localOnly: false, fixedShape: false }
  return { localOnly: persistence === 'browser', fixedShape: true }
}

/**
 * True when a mosaic change on a LOCKED layout is only a resize: same panels,
 * same nesting, only divider positions differ. Locked layouts take these (the
 * user owns panel sizes) and refuse anything that moves, adds or removes a
 * panel (the preset owns that).
 */
export function isResizeOnly(
  current: MosaicNode<string> | null,
  next: MosaicNode<string> | null,
): boolean {
  if (!current || !next) return false
  return mosaicNodesEqual(current, next)
}
